import {
  type DatabaseField,
  DatabaseFieldType,
  type DatabaseValue
} from '@databases/types/database';
import {
  createNumberDisplayFormatter,
  extractNumber
} from '@databases/utils/database/field-types/number-format';
import { createElement, type ReactNode } from 'react';

import { defineFieldReadModule, type FieldReadModule } from '../field-read-module.ts';
import { EMPTY_NUMBER_CONTENT, type NumberReadContent } from './number-read-content.ts';
import { NumberValueView } from './number-value-view.tsx';

function createNumberContent(
  value: string,
  format: (value: string) => string
): NumberReadContent {
  if (!value) {
    return EMPTY_NUMBER_CONTENT;
  }

  const text = format(value);

  return text
    ? Object.freeze<NumberReadContent>({ kind: 'number', text })
    : EMPTY_NUMBER_CONTENT;
}

export type NumberReadRenderer = (content: NumberReadContent) => ReactNode;

const renderNumberValue: NumberReadRenderer = (content) =>
  createElement(NumberValueView, { content });

/**
 * Creates Number's locale-aware, read-only field module.
 */
export function createNumberFieldReadModule({
  locale,
  render = renderNumberValue
}: {
  readonly locale: string;
  readonly render?: NumberReadRenderer;
}): FieldReadModule<NumberReadContent> {
  return defineFieldReadModule<NumberReadContent>({
    fieldType: DatabaseFieldType.NUMBER,
    render,
    compile(field: DatabaseField) {
      const options = field.settings.selectOptions.map((option) => ({
        key: option.id ?? option.label,
        label: option.label
      }));
      const format = createNumberDisplayFormatter(field.settings.numberFormat, locale);

      return Object.freeze({
        fieldId: field.id,
        resolve(
          storedValue: DatabaseValue | undefined,
          defaultValue: DatabaseValue | undefined
        ): NumberReadContent {
          const value = storedValue ?? defaultValue;

          if (value?.type === DatabaseFieldType.NUMBER) {
            return createNumberContent(value.value, format);
          }

          if (value?.type === DatabaseFieldType.TEXT) {
            return createNumberContent(extractNumber(value.value.plain), format);
          }

          if (value?.type === DatabaseFieldType.SELECT) {
            const selected = new Set(value.value);
            const text = options
              .filter((option) => selected.has(option.key))
              .map((option) => option.label)
              .join('\n');

            return createNumberContent(isNaN(Number(text)) ? '' : text, format);
          }

          return EMPTY_NUMBER_CONTENT;
        }
      });
    }
  });
}
